/**
 * Inventory › Serials (سریال‌ها).
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory list archetype (V2DataGrid) with
 * V2Modal forms; docs/design-system/V2_USAGE.md §4 and §6.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Modal,
  V2Tabs,
  V2ToastStack,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { faDigits } from '../../lib/faDigits.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import { displayJalaliDate, displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  LINEAGE_ACTION_LABEL,
  SERIAL_FILTER_KEYS,
  SERIAL_STATUS_META,
  isTruncated,
  labelOf,
  metaOf,
  optionsOf,
  pickerOptions,
} from './inventoryDisplay.js';
import { useDebouncedValue, useToasts } from './useInventoryUi.js';
import './inventory.css';

interface SerialListItem {
  readonly serial_id: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly serial_number: string;
  readonly lot_id: string | null;
  readonly lot_code: string | null;
  readonly status: string;
  readonly warehouse_id: string | null;
  readonly warehouse_name: string | null;
  readonly warehouse_location_id: string | null;
  readonly location_code: string | null;
  readonly last_movement_id: string | null;
  readonly customer_party_id: string | null;
  readonly created_at: string;
}

interface SerialLineageItem {
  readonly lineageId: string;
  readonly serialId: string;
  readonly productId: string;
  readonly movementId: string | null;
  readonly movementType: string;
  readonly fromWarehouseId: string | null;
  readonly fromLocationId: string | null;
  readonly toWarehouseId: string | null;
  readonly toLocationId: string | null;
  readonly partyId: string | null;
  readonly action: string;
  readonly actorId: string;
  readonly notes: string;
  readonly createdAt: string;
}

interface SerialsResponse {
  readonly items: readonly SerialListItem[];
  readonly total: number;
}

interface LotsLookup {
  readonly items: readonly { readonly lot_id: string; readonly lot_code: string }[];
}

type BulkMode = 'list' | 'range';

const PICKER_LIMIT = 200;
const MAX_PADDING = 10;
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'همه' },
  ...optionsOf(SERIAL_STATUS_META, SERIAL_FILTER_KEYS),
];
const BULK_TABS = [
  { id: 'list', label: 'فهرست دستی یا اسکن بارکد' },
  { id: 'range', label: 'تولید خودکار از بازه' },
];

function parseWholeNumber(raw: string): number | null {
  const text = toEnglishDigits(raw).trim();
  if (!/^\d+$/.test(text)) return null;
  const n = Number(text);
  return Number.isSafeInteger(n) ? n : null;
}

function serialAt(prefix: string, n: number, padding: number): string {
  return `${prefix}${String(n).padStart(padding, '0')}`;
}

export function SerialsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');

  // Bulk entry dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<BulkMode>('list');
  const [bulkProductId, setBulkProductId] = useState('');
  const [bulkLotId, setBulkLotId] = useState('');
  const [bulkWarehouseId, setBulkWarehouseId] = useState('');
  const [bulkListText, setBulkListText] = useState('');
  const [bulkPrefix, setBulkPrefix] = useState('SN-');
  const [bulkStart, setBulkStart] = useState('1');
  const [bulkEnd, setBulkEnd] = useState('10');
  const [bulkPadding, setBulkPadding] = useState('4');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Lineage dialog
  const [lineageSerial, setLineageSerial] = useState<SerialListItem | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const url = useMemo(() => {
    if (!slug) return null;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (warehouseFilter) params.set('warehouseId', warehouseFilter);
    return `${base}/api/v1/inventory/serials?${params.toString()}`;
  }, [base, debouncedSearch, slug, statusFilter, warehouseFilter]);

  const result = useApiGet<SerialsResponse>(url, [url]);
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const lots = useApiGet<LotsLookup>(
    slug && bulkOpen && bulkProductId
      ? `${base}/api/v1/inventory/lots?productId=${encodeURIComponent(bulkProductId)}`
      : null,
    [slug, bulkOpen, bulkProductId],
  );
  const lineage = useApiGet<{ items: readonly SerialLineageItem[] }>(
    slug && lineageSerial
      ? `${base}/api/v1/inventory/serials/${lineageSerial.serial_id}/lineage`
      : null,
    [slug, lineageSerial?.serial_id],
  );

  const productOptions = useMemo(
    () => (products.status === 'ready' ? pickerOptions(products.data.items, 'productId') : []),
    [products],
  );
  const productsTruncated =
    products.status === 'ready' && isTruncated(products.data.items.length, products.data.total);
  const warehouseOptions = useMemo(
    () =>
      warehouses.status === 'ready' ? pickerOptions(warehouses.data.items, 'warehouseId') : [],
    [warehouses],
  );
  const warehouseName = useMemo(
    () => new Map(warehouseOptions.map((o) => [o.value, o.label])),
    [warehouseOptions],
  );

  const rangePreview = useMemo(() => {
    if (bulkMode !== 'range') return null;
    const start = parseWholeNumber(bulkStart);
    const end = parseWholeNumber(bulkEnd);
    const pad = bulkPadding.trim() === '' ? 0 : parseWholeNumber(bulkPadding);
    if (start === null || end === null || pad === null || pad > MAX_PADDING || start > end) {
      return null;
    }
    return {
      count: end - start + 1,
      first: serialAt(bulkPrefix, start, pad),
      last: serialAt(bulkPrefix, end, pad),
    };
  }, [bulkEnd, bulkMode, bulkPadding, bulkPrefix, bulkStart]);

  const openBulk = () => {
    setBulkMode('list');
    setBulkProductId('');
    setBulkLotId('');
    setBulkWarehouseId('');
    setBulkListText('');
    setBulkPrefix('SN-');
    setBulkStart('1');
    setBulkEnd('10');
    setBulkPadding('4');
    setBulkError(null);
    setBulkOpen(true);
  };

  const handleBulkSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (bulkSubmitting) return;
    if (!bulkProductId) {
      setBulkError('کالا را انتخاب کنید.');
      return;
    }
    const common = {
      productId: bulkProductId,
      lotId: bulkLotId || undefined,
      warehouseId: bulkWarehouseId || undefined,
    };
    let body: Record<string, unknown>;
    if (bulkMode === 'list') {
      const serialNumbers = bulkListText
        .split(/[\n,،]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (serialNumbers.length === 0) {
        setBulkError('دست‌کم یک شمارهٔ سریال وارد کنید.');
        return;
      }
      if (new Set(serialNumbers).size !== serialNumbers.length) {
        setBulkError('در فهرست، شمارهٔ سریال تکراری وجود دارد.');
        return;
      }
      body = { ...common, serialNumbers };
    } else {
      if (!rangePreview) {
        setBulkError('بازهٔ عددی نامعتبر است؛ شروع باید از پایان کوچک‌تر یا مساوی باشد.');
        return;
      }
      body = {
        ...common,
        prefix: bulkPrefix,
        startNumber: parseWholeNumber(bulkStart),
        endNumber: parseWholeNumber(bulkEnd),
        padding: bulkPadding.trim() === '' ? 0 : parseWholeNumber(bulkPadding),
      };
    }

    setBulkSubmitting(true);
    setBulkError(null);
    try {
      await apiPost(`${base}/api/v1/inventory/serials/bulk`, body);
      setBulkOpen(false);
      push('سریال‌ها ثبت شدند.');
      result.reload();
    } catch (err: unknown) {
      setBulkError(bannerMessageFrom(err, 'ثبت گروهی سریال انجام نشد.'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const columns: V2GridColumn<SerialListItem>[] = [
    {
      key: 'serial',
      header: 'شمارهٔ سریال',
      render: (r) => <span className="inv-code ltr">{r.serial_number}</span>,
    },
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      render: (r) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{r.product_name}</span>
          <span className="inv-cell__sub ltr">{r.product_code}</span>
        </span>
      ),
    },
    {
      key: 'lot',
      header: 'بهر',
      render: (r) =>
        r.lot_code ? <span className="inv-code ltr">{r.lot_code}</span> : <span>—</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (r) => {
        const meta = metaOf(SERIAL_STATUS_META, r.status);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'location',
      header: 'موقعیت فعلی',
      wrap: true,
      render: (r) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{r.warehouse_name || 'خارج از انبار'}</span>
          {r.location_code ? (
            <span className="inv-cell__sub">
              محل: <span className="ltr">{r.location_code}</span>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'registered',
      header: 'تاریخ ثبت',
      muted: true,
      render: (r) => <span className="sk-num">{displayJalaliDate(r.created_at)}</span>,
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (r) => (
        <V2Button size="sm" variant="secondary" onClick={() => setLineageSerial(r)}>
          تاریخچهٔ حرکت
        </V2Button>
      ),
    },
  ];

  if (!slug) return null;

  const total = result.status === 'ready' ? result.data.total : 0;
  const warehousePicker = (
    <select
      className="sk-select inv-toolbar-select"
      aria-label="فیلتر انبار"
      value={warehouseFilter}
      onChange={(e) => setWarehouseFilter(e.target.value)}
    >
      <option value="">همهٔ انبارها</option>
      {warehouseOptions.map((w) => (
        <option key={w.value} value={w.value}>
          {w.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="inv-page">
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            result.error.status === 403
              ? 'دسترسی مشاهدهٔ سریال‌ها را ندارید'
              : 'سریال‌ها قابل نمایش نیستند'
          }
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon="tag"
          title="شماره‌های سریال"
          subtitle={`${faDigits(total)} سریال`}
          search={{ value: search, onChange: setSearch, placeholder: 'سریال، کالا یا بهر' }}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: statusFilter,
              options: STATUS_FILTER_OPTIONS,
              onChange: setStatusFilter,
            },
          ]}
          toolbarExtra={warehouseOptions.length > 0 ? warehousePicker : undefined}
          actions={
            <V2Button variant="primary" icon="plus" onClick={openBulk}>
              ورود گروهی سریال
            </V2Button>
          }
          columns={columns}
          rows={result.data.items}
          rowKey={(r) => r.serial_id}
          zebra
          storageKey="inventory.serials"
          emptyState={
            <V2EmptyState
              icon="tag"
              title="سریالی پیدا نشد"
              description="شمارهٔ سریالی با این جست‌وجو یا فیلتر ثبت نشده است."
              compact
            />
          }
        />
      ) : null}

      <V2Modal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        dismissible={!bulkSubmitting}
        title="ورود گروهی شماره‌های سریال"
        footer={
          <>
            <V2Button variant="ghost" disabled={bulkSubmitting} onClick={() => setBulkOpen(false)}>
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="serial-bulk-form"
              variant="primary"
              disabled={bulkSubmitting}
            >
              {bulkSubmitting ? 'در حال ثبت…' : 'ثبت سریال‌ها'}
            </V2Button>
          </>
        }
      >
        <form id="serial-bulk-form" className="inv-form" noValidate onSubmit={handleBulkSubmit}>
          {bulkError ? <V2Alert tone="danger">{bulkError}</V2Alert> : null}
          <V2FormField
            label="کالا"
            required
            width="full"
            hint={
              productsTruncated
                ? `فقط ${faDigits(PICKER_LIMIT)} کالای اول فهرست نمایش داده می‌شود.`
                : 'کالایی که ردیابی سریال دارد.'
            }
          >
            <select
              className="sk-select"
              value={bulkProductId}
              onChange={(e) => {
                setBulkProductId(e.target.value);
                setBulkLotId('');
              }}
            >
              <option value="">انتخاب کالا…</option>
              {productOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </V2FormField>
          <div className="inv-form__row">
            <V2FormField label="انبار مقصد" width="full" hint="اختیاری">
              <select
                className="sk-select"
                value={bulkWarehouseId}
                onChange={(e) => setBulkWarehouseId(e.target.value)}
              >
                <option value="">بدون انبار</option>
                {warehouseOptions.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField
              label="بهر"
              width="full"
              hint={bulkProductId ? 'برای کالای دارای بهر و سریال' : 'ابتدا کالا را انتخاب کنید'}
            >
              <select
                className="sk-select"
                value={bulkLotId}
                disabled={!bulkProductId || lots.status !== 'ready'}
                onChange={(e) => setBulkLotId(e.target.value)}
              >
                <option value="">بدون بهر</option>
                {lots.status === 'ready'
                  ? lots.data.items.map((l) => (
                      <option key={l.lot_id} value={l.lot_id}>
                        {l.lot_code}
                      </option>
                    ))
                  : null}
              </select>
            </V2FormField>
          </div>

          <V2Tabs
            items={BULK_TABS}
            activeId={bulkMode}
            onChange={(id) => setBulkMode(id as BulkMode)}
          />

          {bulkMode === 'list' ? (
            <V2FormField
              label="شمارهٔ سریال‌ها"
              required
              width="full"
              hint="هر سریال در یک خط، یا با ویرگول جدا شود."
            >
              <textarea
                className="sk-input inv-textarea-mono ltr"
                rows={5}
                placeholder={'SN-1001\nSN-1002\nSN-1003'}
                value={bulkListText}
                onChange={(e) => setBulkListText(e.target.value)}
              />
            </V2FormField>
          ) : (
            <>
              <div className="inv-form__row">
                <V2FormField label="پیشوند" width="full">
                  <input
                    className="sk-input ltr"
                    value={bulkPrefix}
                    onChange={(e) => setBulkPrefix(e.target.value)}
                  />
                </V2FormField>
                <V2FormField
                  label="تعداد ارقام"
                  width="full"
                  hint={`حداکثر ${faDigits(MAX_PADDING)} رقم؛ با صفر پر می‌شود`}
                >
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    value={bulkPadding}
                    onChange={(e) => setBulkPadding(e.target.value)}
                  />
                </V2FormField>
              </div>
              <div className="inv-form__row">
                <V2FormField label="شمارهٔ شروع" required width="full">
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    value={bulkStart}
                    onChange={(e) => setBulkStart(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="شمارهٔ پایان" required width="full">
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    value={bulkEnd}
                    onChange={(e) => setBulkEnd(e.target.value)}
                  />
                </V2FormField>
              </div>
              {rangePreview ? (
                <V2Alert tone="info">
                  {faDigits(rangePreview.count)} سریال ساخته می‌شود:{' '}
                  <span className="ltr inv-code">{rangePreview.first}</span> تا{' '}
                  <span className="ltr inv-code">{rangePreview.last}</span>
                </V2Alert>
              ) : null}
            </>
          )}
        </form>
      </V2Modal>

      <V2Modal
        open={lineageSerial !== null}
        onClose={() => setLineageSerial(null)}
        size="lg"
        title={lineageSerial ? `تاریخچهٔ سریال ${lineageSerial.serial_number}` : 'تاریخچهٔ سریال'}
        footer={
          <V2Button variant="secondary" onClick={() => setLineageSerial(null)}>
            بستن
          </V2Button>
        }
      >
        {lineageSerial ? (
          <p className="inv-form__lead">
            {lineageSerial.product_name} · وضعیت فعلی:{' '}
            {metaOf(SERIAL_STATUS_META, lineageSerial.status).label}
          </p>
        ) : null}
        {lineage.status === 'loading' ? <LoadingState /> : null}
        {lineage.status === 'error' ? (
          <StatePanel
            variant={lineage.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={lineage.error.status === 403 ? 'دسترسی ندارید' : 'تاریخچه قابل نمایش نیست'}
          />
        ) : null}
        {lineage.status === 'ready' && lineage.data.items.length === 0 ? (
          <V2EmptyState
            icon="clock"
            title="تاریخچه‌ای ثبت نشده است"
            description="هنوز هیچ حرکت انباری برای این سریال ثبت نشده است."
            compact
          />
        ) : null}
        {lineage.status === 'ready' && lineage.data.items.length > 0 ? (
          <ol className="inv-timeline">
            {lineage.data.items.map((item) => {
              const from = item.fromWarehouseId ? warehouseName.get(item.fromWarehouseId) : null;
              const to = item.toWarehouseId ? warehouseName.get(item.toWarehouseId) : null;
              return (
                <li key={item.lineageId} className="inv-timeline__item">
                  <div className="inv-timeline__head">
                    <strong>{labelOf(LINEAGE_ACTION_LABEL, item.action)}</strong>
                    <span className="inv-timeline__when sk-num">
                      {displayJalaliDateTime(item.createdAt)}
                    </span>
                  </div>
                  {from || to ? (
                    <div className="inv-timeline__sub">
                      {from ? `از ${from}` : null}
                      {from && to ? ' ' : null}
                      {to ? `به ${to}` : null}
                    </div>
                  ) : null}
                  {item.notes ? <div className="inv-timeline__sub">{item.notes}</div> : null}
                  {item.movementId ? (
                    <Link
                      className="inv-timeline__link"
                      to={`${base}/inventory/movements/${item.movementId}`}
                    >
                      مشاهدهٔ سند انبار
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}
      </V2Modal>

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
